import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@lib/api/client';
import { useAuth } from '../hooks/useAuth';
import type { LoginResponse } from '../types/auth.types';

/**
 * "Landing" del OAuth de Azure AD — vive en el FRONTEND, en la ruta
 * /auth/azure/callback. Esa es la Redirect URI que cada cliente (Adidas,
 * Caja de Ahorro, HNL) registra en SU PROPIO Azure Portal: un dominio público
 * y estable del portal (el mismo que ya usan para acceder), nunca uno del
 * backend interno — ver PLAN_LOGIN_MULTITENANT.md.
 *
 * Microsoft redirige aquí directo con el ?code&state REALES del Authorization
 * Code flow (ya no pasa por el backend). Esta página hace un solo
 * POST /auth/azure/callback con { code, state }.
 *
 * El backend puede responder de dos formas distintas (ver azureAuth.controller.ts):
 * - Sesión completa ({ accessToken, user, sessionId, ... }): el backend logró
 *   loguear directo con TokenAzure + Password="none" (tenant con la relación
 *   de confianza AAD configurada en Aludra, replicando Login.cshtml legacy
 *   para adidas/cajadeahorros) — no hace falta pedir password, se aplica la
 *   sesión y se navega directo al dashboard.
 * - { email, azureRef }: Aludra no tiene esa confianza configurada para este
 *   tenant (o el intento directo falló por cualquier otra razón) — Azure AD
 *   solo verificó/bloqueó el email, y el usuario debe escribir su password de
 *   Aludra en /login (comportamiento previo, sin cambios).
 */
export function AzureCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { applyLoginSuccess } = useAuth();

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code || !state) {
      navigate('/login?azureError=1', { replace: true });
      return;
    }

    apiClient
      .post('/auth/azure/callback', { code, state })
      .then((response) => {
        const data = response.data as { email?: string; azureRef?: string; accessToken?: string };

        // Sesión completa: misma forma que POST /auth/login (accessToken,
        // user, sessionId, ...) — el backend ya logró el login directo.
        if (data.accessToken) {
          void applyLoginSuccess(data as LoginResponse);
          return;
        }

        // Fallback existente: solo email verificado, falta el password real.
        navigate('/login', {
          replace: true,
          state: { azureEmail: data.email, azureRef: data.azureRef },
        });
      })
      .catch(() => {
        navigate('/login?azureError=1', { replace: true });
      });
    // Solo debe correr una vez al montar la página.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-100 dark:bg-secondary-900" data-testid="auth-azure-callback-container">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" data-testid="auth-azure-callback-loading-spinner" />
    </div>
  );
}
